/**
 * Audit Types by Year Chart Component
 * Displays audit types by year with count and surface in a combined chart with dual Y-axis
 */

import { Card } from 'antd';
import {
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import type { TypeByYear } from '../../services/api/auditsDashboardService';

interface AuditTypesByYearChartProps {
  data: TypeByYear[];
}

export const AuditTypesByYearChart: React.FC<AuditTypesByYearChartProps> = ({ data }) => {
  // Group data by year
  const groupedByYear: { [key: number]: any } = {};
  
  data.forEach(item => {
    if (!groupedByYear[item.year]) {
      groupedByYear[item.year] = { year: item.year };
    }
    // Add count for each type (for bars)
    groupedByYear[item.year][`count_${item.audit_type}`] = item.count;
    // Add surface for each type (for lines)
    groupedByYear[item.year][`surface_${item.audit_type}`] = item.surface;
  });

  // Convert to array and sort by year
  const chartData = Object.values(groupedByYear).sort((a, b) => a.year - b.year);

  // Get all unique audit types
  const auditTypes = Array.from(new Set(data.map(item => item.audit_type))).sort();
  
  // Colors for different audit types
  const colors = ['#1890ff', '#52c41a', '#faad14', '#ff4d4f', '#722ed1', '#13c2c2'];

  // Custom tooltip
  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <div style={{ backgroundColor: 'white', padding: '10px', border: '1px solid #ccc', borderRadius: '4px' }}>
          <p style={{ fontWeight: 'bold', marginBottom: '8px' }}>Año: {label}</p>
          {payload.map((entry: any, index: number) => (
            <p key={index} style={{ color: entry.color, margin: '4px 0' }}>
              {entry.name}: {
                entry.name.includes('(ha)') 
                  ? `${entry.value.toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ha`
                  : entry.value
              }
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  return (
    <Card title="Auditorías por Tipo y Año - Número y Superficie">
      <ResponsiveContainer width="100%" height={450}>
        <ComposedChart data={chartData}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="year" />
          <YAxis
            yAxisId="left"
            label={{ value: 'Nº Auditorías', angle: -90, position: 'insideLeft' }}
          />
          <YAxis
            yAxisId="right"
            orientation="right"
            tickFormatter={(value) =>
              new Intl.NumberFormat('es-ES', {
                notation: 'compact',
                compactDisplay: 'short',
              }).format(value)
            }
            label={{ value: 'Superficie (ha)', angle: 90, position: 'insideRight' }}
          />
          <Tooltip content={<CustomTooltip />} />
          <Legend />
          
          {/* Bars for count (left axis) - one bar per audit type */}
          {auditTypes.map((type, index) => (
            <Bar
              key={`count-${type}`}
              yAxisId="left"
              dataKey={`count_${type}`}
              fill={colors[index % colors.length]}
              name={`${type} (Nº Auditorías)`}
            />
          ))}
          
          {/* Lines for surface (right axis) - one line per audit type */}
          {auditTypes.map((type, index) => (
            <Line
              key={`surface-${type}`}
              yAxisId="right"
              type="monotone"
              dataKey={`surface_${type}`}
              stroke={colors[index % colors.length]}
              strokeWidth={3}
              name={`${type} (Superficie ha)`}
              dot={{ r: 5, fill: colors[index % colors.length], strokeWidth: 2, stroke: '#fff' }}
              strokeDasharray="5 5"
            />
          ))}
        </ComposedChart>
      </ResponsiveContainer>
    </Card>
  );
};
